import React, { useEffect } from 'react';
import { navigateTo } from './shared.js';

/* ---------- INLINE STYLES ---------- */
const LEGAL_STYLES = `
  .legal-page {
    min-height: 100vh;
    background: var(--bg, #f9f7f3);
    color: var(--ink, #1a1814);
    font-family: var(--serif, Georgia, 'Times New Roman', serif);
    display: flex;
    flex-direction: column;
  }
  .legal-header {
    max-width: 720px;
    margin: 0 auto;
    padding: 48px 24px 32px;
    width: 100%;
    box-sizing: border-box;
  }
  .legal-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--mono, 'Courier New', monospace);
    font-size: 12px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--ink-3, #6b6760);
    text-decoration: none;
    margin-bottom: 32px;
    transition: color 0.15s ease;
  }
  .legal-back:hover { color: var(--ink, #1a1814); }
  .legal-title {
    font-size: clamp(24px, 4vw, 36px);
    font-weight: 600;
    line-height: 1.2;
    margin: 0 0 12px;
    color: var(--ink, #1a1814);
  }
  .legal-updated {
    font-family: var(--mono, 'Courier New', monospace);
    font-size: 12px;
    color: var(--ink-3, #6b6760);
    margin: 0;
  }
  .legal-body {
    max-width: 720px;
    margin: 0 auto;
    padding: 0 24px 64px;
    width: 100%;
    box-sizing: border-box;
    flex: 1;
    line-height: 1.75;
    font-size: 16px;
  }
  .legal-body h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 40px 0 12px;
    color: var(--ink, #1a1814);
    border-bottom: 1px solid var(--rule, #e5e1d8);
    padding-bottom: 8px;
  }
  .legal-body h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 24px 0 8px;
    color: var(--ink, #1a1814);
  }
  .legal-body p {
    margin: 0 0 16px;
    color: var(--ink-2, #3d3b37);
  }
  .legal-body ul, .legal-body ol {
    margin: 0 0 16px;
    padding-left: 24px;
    color: var(--ink-2, #3d3b37);
  }
  .legal-body li { margin-bottom: 8px; }
  .legal-body a {
    color: var(--on, #2d7a4f);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .legal-body a:hover { opacity: 0.8; }
  .legal-body code {
    font-family: var(--mono, 'Courier New', monospace);
    font-size: 13px;
    background: var(--bg-2, #f0ede7);
    padding: 2px 6px;
    border-radius: 3px;
    color: var(--ink, #1a1814);
  }
  .legal-body .placeholder {
    background: oklch(0.95 0.02 60);
    border: 1px dashed oklch(0.7 0.06 60);
    border-radius: 3px;
    padding: 2px 6px;
    font-style: italic;
    color: oklch(0.45 0.08 60);
    font-family: var(--mono, 'Courier New', monospace);
    font-size: 13px;
  }
  .legal-body table {
    width: 100%;
    border-collapse: collapse;
    margin: 24px 0;
    font-size: 13px;
    line-height: 1.5;
    overflow-x: auto;
    display: block;
  }
  .legal-body thead { background: var(--bg-2, #f0ede7); }
  .legal-body th {
    text-align: left;
    padding: 10px 12px;
    font-weight: 600;
    border: 1px solid var(--rule, #e5e1d8);
    color: var(--ink, #1a1814);
    white-space: nowrap;
  }
  .legal-body td {
    padding: 10px 12px;
    border: 1px solid var(--rule, #e5e1d8);
    color: var(--ink-2, #3d3b37);
    vertical-align: top;
  }
  .legal-body tr:nth-child(even) td { background: var(--bg-2, #f0ede7); }
  .legal-body .notice-box {
    border-left: 3px solid var(--on, #2d7a4f);
    background: oklch(0.96 0.02 144);
    padding: 16px 20px;
    margin: 24px 0;
    border-radius: 0 4px 4px 0;
  }
  .legal-body .notice-box p { margin: 0; color: var(--ink, #1a1814); }
  .legal-footer {
    max-width: 720px;
    margin: 0 auto;
    padding: 24px 24px 48px;
    width: 100%;
    box-sizing: border-box;
    border-top: 1px solid var(--rule, #e5e1d8);
    font-family: var(--mono, 'Courier New', monospace);
    font-size: 12px;
    color: var(--ink-3, #6b6760);
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  .legal-footer a {
    color: var(--ink-2, #3d3b37);
    text-decoration: none;
  }
  .legal-footer a:hover { text-decoration: underline; }
  @media (max-width: 600px) {
    .legal-header { padding: 32px 16px 24px; }
    .legal-body { padding: 0 16px 48px; font-size: 15px; }
    .legal-footer { padding: 20px 16px 40px; }
  }
`;

/* ---------- SHARED CHROME ---------- */
function LegalPageChrome({ titleEs, titleAr, dialect, children, lastUpdated }) {
  const ar = dialect === 'rioplatense';
  const title = ar ? (titleAr || titleEs) : titleEs;
  const backLabel = 'Volver al inicio';

  useEffect(() => {
    const prev = document.title;
    document.title = `${title} — EnAutomático`;
    const metaEl = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaEl.setAttribute('name', 'description');
    metaEl.setAttribute('content', `${title}: información legal de EnAutomático para el servicio de diagnóstico de IA.`);
    if (!metaEl.parentNode) document.head.appendChild(metaEl);
    return () => { document.title = prev; };
  }, [title]);

  return (
    <div className="legal-page">
      <style>{LEGAL_STYLES}</style>
      <header className="legal-header">
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigateTo('/'); }}
          className="legal-back"
          aria-label={backLabel}
        >
          ← {backLabel}
        </a>
        <h1 className="legal-title">{title}</h1>
        <p className="legal-updated">Última actualización: {lastUpdated}</p>
      </header>
      <main className="legal-body">
        {children}
      </main>
      <footer className="legal-footer">
        <a href="/privacidad" onClick={(e) => { e.preventDefault(); navigateTo('/privacidad'); }}>Privacidad</a>
        <span>·</span>
        <a href="/aviso-legal" onClick={(e) => { e.preventDefault(); navigateTo('/aviso-legal'); }}>Aviso legal</a>
        <span>·</span>
        <a href="/cookies" onClick={(e) => { e.preventDefault(); navigateTo('/cookies'); }}>Cookies</a>
        <span>·</span>
        <a href="mailto:derechos@enautomatico.pro">derechos@enautomatico.pro</a>
      </footer>
    </div>
  );
}

/* ================================================================
   PRIVACIDAD PAGE
   Closes: GDPR-01 + FNNL-03
   ================================================================ */
export function PrivacidadPage({ dialect }) {
  return (
    <LegalPageChrome
      titleEs="Política de Privacidad"
      dialect={dialect}
      lastUpdated="28 de abril de 2026"
    >
      {/* 1. Quiénes somos */}
      <h2>1. Quiénes somos (Responsable del tratamiento)</h2>
      <p>
        <strong>Razón social:</strong> <span className="placeholder">[COMPLETAR — nombre completo del operador / razón social antes de publicar]</span><br />
        <strong>NIF/NIE/CIF:</strong> <span className="placeholder">[COMPLETAR antes de publicar]</span><br />
        <strong>Domicilio:</strong> Barcelona, España <span className="placeholder">[COMPLETAR dirección completa]</span><br />
        <strong>Contacto del titular de los datos:</strong> <a href="mailto:derechos@enautomatico.pro">derechos@enautomatico.pro</a>
      </p>
      <p>
        En adelante, <strong>"EnAutomático"</strong> o <strong>"nosotros"</strong>. Operamos bajo la marca EnAutomático
        y prestamos un servicio de diagnóstico de eficiencia operativa mediante inteligencia artificial para PYMES
        de habla hispana.
      </p>

      {/* 2. Qué datos recogemos y para qué */}
      <h2>2. Qué datos recogemos y para qué (finalidades y bases jurídicas)</h2>

      <h3>2.1 Reserva del diagnóstico (Cal.com)</h3>
      <p>
        Cuando reservas una llamada de diagnóstico a través de nuestro formulario, recogemos: nombre y apellidos,
        correo electrónico, teléfono en formato E.164, nombre de empresa, sitio web, ciudad, sector de actividad,
        rol en la empresa, tamaño del equipo (nº de empleados), facturación anual aproximada, herramientas que
        usas actualmente, y dolor operativo principal.
      </p>
      <p>
        <strong>Base jurídica:</strong> Art. 6(1)(b) RGPD — ejecución del contrato (necesario para prestarte el
        servicio de diagnóstico contratado).
      </p>

      <h3>2.2 Llamada con Sofía (grabación y transcripción)</h3>
      <p>
        Sofía es nuestra asistente de IA de voz. Al inicio de cada llamada te informa explícitamente de que se
        trata de un sistema de IA (cumpliendo el Art. 50 del EU AI Act — ver sección 9 de esta política) y te
        anuncia el propósito de la conversación: hacerte unas preguntas rápidas sobre tu negocio para entregarte
        el diagnóstico en 48 horas. Durante la llamada se almacenan: grabación de audio completa, transcripción
        textual de la conversación y metadatos de la llamada (duración, hora, resultado).
      </p>
      <p>
        <strong>Base jurídica:</strong> Art. 6(1)(b) RGPD — medidas precontractuales adoptadas a petición del
        interesado. Al reservar la llamada en nuestra agenda has solicitado expresamente el servicio de
        diagnóstico, y la grabación es necesaria para prestarlo. La información requerida por el Art. 13 RGPD
        (sub-encargados, retención, derechos) se te facilita en esta misma política, enlazada desde el formulario
        de reserva antes de confirmarla.
      </p>
      <p>
        <strong>Derecho de oposición (Art. 21 RGPD):</strong> al inicio de la llamada Sofía te pregunta
        "¿Empezamos?". Si respondes "no" o permaneces en silencio durante más de 8 segundos, Sofía cierra la
        llamada con un saludo breve y no se registra transcripción ni se procesa el diagnóstico. La grabación
        parcial (típicamente menor a 30 segundos) queda en el almacenamiento interno de Retell.ai durante un
        máximo de 30 días, conforme a su política de retención por defecto, y luego se elimina automáticamente.
      </p>

      <h3>2.3 Generación del informe diagnóstico (Google Gemini)</h3>
      <p>
        Tu transcripción completa se envía a la API de Google Gemini 2.5 Flash para generar el markdown del
        informe de diagnóstico personalizado. El informe resultante se almacena en nuestro CRM (Notion) y el
        deck HTML se publica en <code>enautomatico-decks.vercel.app/&#123;tu-slug&#125;</code>, accesible solo
        mediante la URL única que te enviamos.
      </p>
      <p>
        <strong>Base jurídica:</strong> Art. 6(1)(b) RGPD — ejecución del contrato.
      </p>

      <h3>2.4 Llamada de revisión</h3>
      <p>
        Cuando agendas la llamada de revisión de 30 minutos (evento Cal.com <code>sborzone/revision</code>),
        registramos las notas operativas de la reunión en nuestro CRM.
      </p>
      <p>
        <strong>Base jurídica:</strong> Art. 6(1)(b) RGPD — ejecución del contrato.
      </p>

      <h3>2.5 Captación de prospectos (LinkedIn)</h3>
      <p>
        Si iniciamos contacto a través de LinkedIn u otras redes profesionales, procesamos únicamente datos
        públicos del perfil profesional (nombre, empresa, cargo, ciudad).
      </p>
      <p>
        <strong>Base jurídica:</strong> Art. 6(1)(f) RGPD — interés legítimo del responsable en la prospección
        B2B en red profesional pública.
      </p>

      {/* 3. Sub-encargados */}
      <h2>3. Sub-encargados del tratamiento (Art. 28 RGPD)</h2>
      <p>
        Contamos con los siguientes sub-encargados del tratamiento. Todos tienen acceso únicamente a los datos
        necesarios para la función operativa descrita, conforme a acuerdos Art. 28 firmados o en tramitación.
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Sub-encargado</th>
              <th>Función operativa</th>
              <th>Categoría de datos</th>
              <th>Ubicación / Mecanismo de transferencia</th>
              <th>Retención por defecto</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Twilio Inc.</strong></td>
              <td>Terminación SIP del número español <code>+34930340857</code> (telefonía entrante/saliente para Sofía)</td>
              <td>Audio en tránsito (no almacenado en Twilio), registros CDR</td>
              <td>EE. UU. — Standard Contractual Clauses (SCCs)</td>
              <td>30 días para CDR; audio NO almacenado en Twilio</td>
            </tr>
            <tr>
              <td><strong>Retell.ai</strong></td>
              <td>Orquestación del agente de voz Sofía (ASR, TTS, lógica de conversación, webhook <code>call_analyzed</code>)</td>
              <td>Audio de la llamada, transcripción completa, metadatos de llamada</td>
              <td>EE. UU. — SCCs</td>
              <td>≤30 días (configuración por defecto). Borrado manual disponible si ejerces el Art. 17.</td>
            </tr>
            <tr>
              <td><strong>ElevenLabs</strong></td>
              <td>Síntesis de voz de Sofía (TTS), invocado dentro de Retell</td>
              <td>Texto generado por el modelo para la respuesta hablada (sin PII directa del titular)</td>
              <td>EE. UU. — Cubierto por DPA de Retell como sub-sub-encargado</td>
              <td>Sin retención persistente (procesamiento en tránsito)</td>
            </tr>
            <tr>
              <td><strong>Google LLC (Gemini 2.5 Flash)</strong></td>
              <td>Análisis de transcripción y generación del informe diagnóstico post-llamada + análisis de datos del formulario y generación del informe-teaser pre-llamada, ambos vía API de Google Generative Language</td>
              <td>Transcripción completa + datos del prospecto (nombre, empresa, sector, dolor principal, herramientas). Pre-llamada: solo datos del formulario; post-llamada: transcripción completa más datos del formulario.</td>
              <td>EE. UU. — SCCs (Google Cloud Data Processing Amendment)</td>
              <td>Sin retención en modo API; datos NO usados para entrenar modelos con clave propia</td>
            </tr>
            <tr>
              <td><strong>Cal.com Inc.</strong></td>
              <td>Gestión del calendario y formulario de booking del diagnóstico y revisión</td>
              <td>Datos del formulario de reserva: nombre, email, teléfono, empresa, ciudad, sector, rol, tamaño, facturación, herramientas, dolor principal</td>
              <td>UE (instancia EU-hosted de Cal.com)</td>
              <td>Indefinido en Cal.com hasta borrado manual; datos espejados a Notion CRM vía webhook</td>
            </tr>
            <tr>
              <td><strong>Make.com (Celonis SE)</strong></td>
              <td>Orquestación de los 5 flujos de automatización que conectan Cal.com → Notion → Retell → Gemini → Telegram</td>
              <td>Payloads de webhooks con todos los campos del booking, transcripción completa y markdown del informe</td>
              <td>UE (AWS eu-west, instancia eu2.make.com) — datos no salen de la UE en este sub-encargado</td>
              <td>30 días en logs de ejecución de Make</td>
            </tr>
            <tr>
              <td><strong>Notion Labs Inc.</strong></td>
              <td>CRM canónica — almacenamiento del perfil del prospecto, transcripción resumida y markdown completo del informe</td>
              <td>Todos los campos del booking, <code>call_summary</code>, markdown del informe (3 campos), <code>recording_url</code>, <code>retention_purge_date</code>, LTV</td>
              <td>EE. UU. — SCCs (GDPR Addendum de Notion)</td>
              <td>Indefinido hasta <code>retention_purge_date</code> o borrado manual por solicitud del interesado</td>
            </tr>
            <tr>
              <td><strong>Telegram FZ-LLC</strong></td>
              <td>Alertas operativas internas al operador (dispatched, completed, short-call, no-answer, cancelled)</td>
              <td>Nombre del prospecto, empresa y fecha del diagnóstico (datos mínimos para identificar la alerta). No se envía transcripción ni informe.</td>
              <td>EE. UU. / EAU — Sin DPA pública; uso estrictamente operativo con minimización de datos</td>
              <td>Indefinido en historial del chat (datos minimizados)</td>
            </tr>
            <tr>
              <td><strong>Vercel Inc.</strong></td>
              <td>Hosting estático del sitio <code>enautomatico.pro</code> y de los decks HTML en <code>enautomatico-decks.vercel.app</code></td>
              <td>Logs HTTP estándar (IP anonimizada, user-agent, ruta). Los formularios redirigen a Cal.com — Vercel NO recibe datos del formulario.</td>
              <td>EE. UU. con edge CDN en la UE — SCCs</td>
              <td>30 días para logs HTTP de acceso</td>
            </tr>
            <tr>
              <td><strong>Plausible Insights OÜ</strong></td>
              <td>Analítica web sin cookies del sitio <code>enautomatico.pro</code> (conforme al RGPD sin banner de cookies)</td>
              <td>Eventos de página agregados, sin identificadores persistentes del usuario, sin cookies de seguimiento</td>
              <td>UE (Estonia / Alemania, hosting 100% europeo)</td>
              <td>30 días para datos de eventos detallados; agregados indefinidamente sin identificadores</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="notice-box">
        <p>
          Esta lista refleja exclusivamente el stack desplegado a la fecha de esta política. Ante cualquier
          incorporación de un nuevo sub-encargado, actualizaremos esta tabla antes de procesar datos a
          través del nuevo servicio.
        </p>
      </div>

      {/* 4. Retención */}
      <h2>4. Cuánto tiempo conservamos tus datos</h2>
      <ul>
        <li>
          <strong>Grabación de audio:</strong> ≤30 días en Retell.ai (configuración por defecto alineada con
          GDPR-04). Borrado automático o manual si ejerces el derecho de supresión (Art. 17).
        </li>
        <li>
          <strong>Transcripción del informe:</strong> 90 días por defecto desde la fecha del diagnóstico.
          Extendible únicamente con tu consentimiento escrito explícito para una finalidad concreta adicional.
        </li>
        <li>
          <strong>Datos del booking y markdown del informe (Notion CRM):</strong> Indefinidos hasta tu
          petición de borrado o hasta la fecha de purga programada (<code>retention_purge_date</code>) que
          el operador fija en el CRM.
        </li>
        <li>
          <strong>Logs HTTP (Vercel):</strong> 30 días.
        </li>
        <li>
          <strong>Eventos analíticos (Plausible):</strong> 30 días para datos de eventos detallados; datos
          agregados conservados indefinidamente sin identificadores persistentes.
        </li>
        <li>
          <strong>Registros CDR (Twilio):</strong> 30 días.
        </li>
        <li>
          <strong>Logs de automatización (Make):</strong> 30 días en logs de ejecución de escenarios.
        </li>
        <li>
          <strong>Datos de outreach LinkedIn:</strong> 6 meses desde el último contacto activo. Baja
          inmediata y definitiva si lo solicitas.
        </li>
      </ul>

      {/* 5. Derechos */}
      <h2>5. Tus derechos (Arts. 15–22 RGPD)</h2>
      <p>Como titular de los datos tienes derecho a:</p>
      <ul>
        <li><strong>Acceso (Art. 15):</strong> saber qué datos tenemos sobre ti.</li>
        <li><strong>Rectificación (Art. 16):</strong> corregir datos inexactos o incompletos.</li>
        <li><strong>Supresión / derecho al olvido (Art. 17):</strong> solicitar el borrado de tus datos.</li>
        <li><strong>Limitación del tratamiento (Art. 18):</strong> restringir el uso de tus datos en determinadas circunstancias.</li>
        <li><strong>Portabilidad (Art. 20):</strong> recibir tus datos en formato estructurado y legible por máquina.</li>
        <li><strong>Oposición (Art. 21):</strong> oponerte al tratamiento, especialmente cuando la base jurídica es el interés legítimo.</li>
        <li><strong>Retirada del consentimiento (Art. 7.3):</strong> retirar en cualquier momento el consentimiento prestado, sin que ello afecte a la licitud del tratamiento previo.</li>
      </ul>
      <p>
        Para ejercer cualquiera de estos derechos, escríbenos a{' '}
        <a href="mailto:derechos@enautomatico.pro">derechos@enautomatico.pro</a>. Respondemos en un plazo
        de <strong>30 días naturales</strong> (Art. 12.3 RGPD), ampliable a 3 meses en casos de especial
        complejidad, previa comunicación dentro del primer mes.
      </p>

      {/* 6. AEPD */}
      <h2>6. Reclamaciones ante la AEPD</h2>
      <p>
        Si consideras que no estamos cumpliendo esta política o el RGPD, tienes derecho a presentar una
        reclamación ante la Agencia Española de Protección de Datos (AEPD) en{' '}
        <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">www.aepd.es</a>.
      </p>

      {/* 7. Transferencias internacionales */}
      <h2>7. Transferencias internacionales de datos</h2>
      <p>
        Varios sub-encargados tienen sede en EE. UU.: Twilio, Retell.ai, ElevenLabs, Google LLC, Notion Labs
        y Vercel. Para todas estas transferencias aplicamos las <strong>Cláusulas Contractuales Tipo (CCT /
        Standard Contractual Clauses)</strong> aprobadas por la Comisión Europea, como mecanismo de
        salvaguarda de las transferencias internacionales de datos según el Art. 46 RGPD. El detalle del
        mecanismo por sub-encargado está en la tabla de la sección 3.
      </p>
      <p>
        Make.com opera íntegramente en la UE (instancia <code>eu2.make.com</code>, AWS eu-west). Cal.com usa
        instancia EU-hosted. Plausible Insights OÜ tiene infraestructura 100% europea (Estonia / Alemania).
      </p>

      {/* 8. Cambios a la política */}
      <h2>8. Cambios a esta política</h2>
      <p>
        Notificaremos cualquier cambio material por correo electrónico a los titulares activos y publicaremos
        la fecha de actualización al inicio de esta página. Los cambios entran en vigor en el momento de su
        publicación.
      </p>

      {/* 9. EU AI Act Art. 50 */}
      <h2>9. EU AI Act Art. 50 — Transparencia del agente de IA</h2>
      <p>
        Sofía es un agente de voz de inteligencia artificial (no una persona humana). En cumplimiento del
        Art. 50 del Reglamento Europeo de Inteligencia Artificial (EU AI Act, Reglamento UE 2024/1689),
        cuando Sofía te llama tras tu reserva en nuestra agenda, te informa explícitamente en los primeros
        segundos de la llamada de que estás hablando con un <strong>asistente de IA</strong> y te anuncia el
        propósito del diagnóstico antes de iniciar las preguntas.
      </p>
      <p>
        Si en cualquier momento decides no continuar la llamada, simplemente indícalo (o permanece en silencio)
        y Sofía cerrará la conversación con un saludo breve. No se generará transcripción ni se procesará el
        diagnóstico. La grabación parcial queda en el almacenamiento interno de Retell.ai durante un máximo de
        30 días y luego se elimina automáticamente conforme a su política de retención por defecto. Esto da
        efecto a tu derecho de oposición (Art. 21 RGPD).
      </p>
    </LegalPageChrome>
  );
}

/* ================================================================
   AVISO LEGAL PAGE
   Closes: FNNL-04 (LSSI-CE Art. 10)
   ================================================================ */
export function AvisoLegalPage({ dialect }) {
  return (
    <LegalPageChrome
      titleEs="Aviso Legal"
      dialect={dialect}
      lastUpdated="28 de abril de 2026"
    >
      {/* 1. Titular */}
      <h2>1. Titular del sitio web (Art. 10 LSSI-CE)</h2>
      <p>
        En cumplimiento del Art. 10 de la Ley 34/2002 de Servicios de la Sociedad de la Información y del
        Comercio Electrónico (LSSI-CE), se informa:
      </p>
      <ul>
        <li>
          <strong>Razón social:</strong>{' '}
          <span className="placeholder">[COMPLETAR — nombre completo del operador / razón social]</span>
        </li>
        <li>
          <strong>NIF/NIE/CIF:</strong>{' '}
          <span className="placeholder">[COMPLETAR antes de publicar]</span>
        </li>
        <li>
          <strong>Domicilio:</strong> Barcelona, España{' '}
          <span className="placeholder">[COMPLETAR dirección completa]</span>
        </li>
        <li>
          <strong>Correo electrónico de contacto:</strong>{' '}
          <a href="mailto:derechos@enautomatico.pro">derechos@enautomatico.pro</a>
        </li>
        <li>
          <strong>Sitio web:</strong>{' '}
          <a href="https://enautomatico.pro" target="_blank" rel="noopener noreferrer">enautomatico.pro</a>
        </li>
      </ul>

      {/* 2. Objeto del sitio */}
      <h2>2. Objeto del sitio web</h2>
      <p>
        <strong>enautomatico.pro</strong> presenta el servicio de diagnóstico de eficiencia operativa mediante
        inteligencia artificial de EnAutomático, dirigido a propietarios y responsables de PYMES de habla
        hispana (España y Argentina).
      </p>
      <p>
        El servicio consiste en una entrevista de voz de 20–30 minutos con el agente de IA Sofía, seguida de
        la entrega de un informe diagnóstico personalizado en formato HTML en un plazo de 48 horas, y una
        llamada de revisión de 30 minutos con el operador.
      </p>
      <p>
        La reserva del diagnóstico se realiza exclusivamente a través del formulario integrado en la ruta{' '}
        <code>/diagnostico</code> del sitio, que usa la plataforma Cal.com.
      </p>

      {/* 3. Condiciones de uso */}
      <h2>3. Condiciones de uso</h2>
      <p>
        El contenido de este sitio es de carácter informativo y comercial. Al reservar una llamada de
        diagnóstico, el usuario acepta:
      </p>
      <ul>
        <li>
          La <a href="/privacidad" onClick={(e) => { e.preventDefault(); navigateTo('/privacidad'); }}>
            Política de Privacidad
          </a> de EnAutomático.
        </li>
        <li>
          La grabación y transcripción de la llamada con Sofía, necesarias para prestar el servicio de
          diagnóstico que el usuario solicita al reservar (base jurídica: Art. 6(1)(b) RGPD — medidas
          precontractuales). Sofía anuncia explícitamente al inicio de la llamada que es un asistente de IA y
          el propósito de la conversación; el usuario puede ejercer su derecho de oposición (Art. 21 RGPD)
          indicando que no desea continuar.
        </li>
        <li>
          Las presentes condiciones de uso.
        </li>
      </ul>
      <p>
        El uso del sitio web implica la aceptación de las presentes condiciones. Si no estás de acuerdo,
        te rogamos que abandones el sitio.
      </p>

      {/* 4. Propiedad intelectual */}
      <h2>4. Propiedad intelectual</h2>
      <p>
        Todo el contenido de este sitio web — incluyendo, sin limitación: textos, imágenes, logotipos,
        marca <strong>EnAutomático</strong>, identidad visual, plantillas de informes de diagnóstico, deck
        HTML generado por la skill <code>/enautomatico-deck</code>, y código fuente — es propiedad de
        EnAutomático o de terceros que han autorizado su uso.
      </p>
      <p>
        Queda prohibida la reproducción, distribución, comunicación pública o transformación de estos
        contenidos sin autorización expresa y por escrito de EnAutomático, salvo en los supuestos
        legalmente previstos.
      </p>

      {/* 5. Responsabilidad */}
      <h2>5. Limitación de responsabilidad</h2>
      <p>
        EnAutomático se esfuerza por mantener el sitio disponible y actualizado, pero no garantiza la
        disponibilidad continua del servicio (objetivo de uptime razonable del 99% anual). No nos hacemos
        responsables de daños derivados de interrupciones técnicas o fallos de terceros proveedores
        (Vercel, Retell, Cal.com, etc.).
      </p>
      <p>
        Los informes diagnósticos generados por EnAutomático son <strong>recomendaciones operativas</strong>{' '}
        basadas en la información proporcionada durante la llamada. No constituyen asesoramiento legal,
        fiscal, contable ni de ningún otro tipo que requiera habilitación profesional regulada. Para
        cuestiones legales o fiscales, consulta con un gestor o abogado habilitado.
      </p>

      {/* 6. Sitios externos */}
      <h2>6. Enlaces a sitios externos</h2>
      <p>
        Este sitio puede enlazar a servicios externos (Cal.com, Vercel, Plausible, y otros sub-encargados
        listados en la{' '}
        <a href="/privacidad" onClick={(e) => { e.preventDefault(); navigateTo('/privacidad'); }}>
          Política de Privacidad
        </a>). EnAutomático no se hace responsable del contenido ni de las políticas de privacidad de
        sitios web de terceros. Te recomendamos que consultes las políticas individuales de cada servicio
        antes de usarlos.
      </p>

      {/* 7. Ley aplicable */}
      <h2>7. Ley aplicable y jurisdicción</h2>
      <p>
        Las presentes condiciones se rigen por la legislación española. Para la resolución de cualquier
        controversia derivada del uso de este sitio web, las partes se someten, con renuncia expresa a
        cualquier otro fuero que pudiera corresponderles, a los Juzgados y Tribunales de <strong>Barcelona
        (España)</strong>.
      </p>
      <p>
        Para clientes internacionales fuera de España, la jurisdicción será la del domicilio del titular
        de EnAutomático, salvo que la legislación local imperativa de protección de datos del cliente
        disponga otra cosa.
      </p>
    </LegalPageChrome>
  );
}

/* ================================================================
   COOKIES PAGE
   Closes: FNNL-04 (cookie policy)
   ================================================================ */
export function CookiesPage({ dialect }) {
  const ar = dialect === 'rioplatense';

  return (
    <LegalPageChrome
      titleEs="Política de Cookies"
      dialect={dialect}
      lastUpdated="28 de abril de 2026"
    >
      {/* 1. ¿Usamos cookies? */}
      <h2>1. ¿Usamos cookies?</h2>
      <div className="notice-box">
        <p>
          <strong>No usamos cookies de seguimiento ni publicitarias.</strong> Nuestra analítica (Plausible
          Insights OÜ) es GDPR-nativa sin cookies — por eso no mostramos banner de cookies.
        </p>
      </div>
      <p>
        El sitio <strong>enautomatico.pro</strong> utiliza <strong>Plausible Insights OÜ</strong> (con sede
        en Estonia / Alemania, infraestructura 100% europea) como herramienta de analítica web. Plausible
        funciona sin cookies de seguimiento: utiliza hashes anónimos diarios sin ningún identificador
        persistente almacenado en el navegador del visitante.
      </p>
      <p>
        Por este diseño, <strong>no estamos obligados a mostrar un banner de cookies</strong> conforme al
        criterio de la AEPD y las directrices europeas sobre analítica web sin cookies de seguimiento.
      </p>

      {/* 2. Qué se almacena en tu navegador */}
      <h2>2. ¿Qué se almacena en tu navegador?</h2>
      <p>Únicamente lo siguiente:</p>

      <h3>2.1 <code>localStorage: ea_dialect</code> (almacenamiento local, no es una cookie)</h3>
      <p>
        Cuando visitas el sitio, detectamos automáticamente tu dialecto lingüístico (castellano para España
        vs. rioplatense para Argentina y Uruguay) a partir de la configuración de idioma de tu navegador
        (<code>navigator.languages</code>). Si el dialecto detectado es inequívoco, guardamos el resultado
        en <code>localStorage</code> bajo la clave <code>ea_dialect</code> para no repetir la detección en
        visitas siguientes.
      </p>
      <p>
        <strong>Características de este almacenamiento:</strong>
      </p>
      <ul>
        <li>No es una cookie — vive en <code>localStorage</code>, no en las cabeceras HTTP.</li>
        <li>No se transmite a ningún servidor externo.</li>
        <li>Solo contiene el valor <code>"castellano"</code> o <code>"rioplatense"</code>.</li>
        <li>No contiene ningún identificador tuyo.</li>
        <li>Puedes borrarlo en cualquier momento desde la configuración de tu navegador (ver sección 4).</li>
      </ul>

      <h3>2.2 Nada más</h3>
      <p>
        EnAutomático no establece ninguna otra cookie, identificador persistente, pixel de seguimiento,
        ni mecanismo de fingerprinting en tu navegador al navegar por el sitio.
      </p>

      {/* 3. Cookies de terceros */}
      <h2>3. Cookies de terceros (Cal.com)</h2>
      <p>
        Cuando visitas la ruta <code>/diagnostico</code> y <strong>interactúas con el widget de reserva de
        Cal.com</strong>, <strong>Cal.com Inc. sí puede establecer cookies técnicas necesarias</strong> para
        que su sistema de booking funcione correctamente (por ejemplo, para mantener la sesión del formulario
        de reserva).
      </p>
      <p>
        Estas cookies son responsabilidad exclusiva de Cal.com Inc. y se rigen por su propia política de
        privacidad y cookies, disponible en{' '}
        <a href="https://cal.com/privacy" target="_blank" rel="noopener noreferrer">cal.com/privacy</a>.
        EnAutomático no controla esas cookies ni tiene acceso a su contenido.
      </p>
      <p>
        Las cookies de Cal.com solo se activan cuando interactúas con el widget de reserva — no al
        simplemente visitar enautomatico.pro.
      </p>

      {/* 4. Cómo eliminar localStorage */}
      <h2>4. Cómo borrar <code>ea_dialect</code> de tu navegador</h2>
      <p>
        Si {ar ? 'querés' : 'quieres'} eliminar el valor guardado en <code>localStorage</code>, sigue estas
        instrucciones según tu navegador:
      </p>
      <ul>
        <li>
          <strong>Chrome / Edge:</strong> Configuración → Privacidad y seguridad → Borrar datos de
          navegación → Selecciona "Cookies y otros datos de sitios" → Borrar datos.
          O bien: Herramientas de desarrollador (<kbd>F12</kbd>) → Application → Local Storage →
          <code>enautomatico.pro</code> → elimina la clave <code>ea_dialect</code>.
        </li>
        <li>
          <strong>Firefox:</strong> Configuración → Privacidad y seguridad → Cookies y datos del sitio →
          Limpiar datos. O: Herramientas del navegador (<kbd>F12</kbd>) → Almacenamiento →
          Almacenamiento local → elimina <code>ea_dialect</code>.
        </li>
        <li>
          <strong>Safari:</strong> Preferencias → Privacidad → Administrar datos de sitio web → busca
          <code>enautomatico.pro</code> → Eliminar.
        </li>
        <li>
          <strong>Safari (iOS):</strong> Ajustes → Safari → Borrar historial y datos de sitios web.
        </li>
      </ul>
      <p>
        Tras borrarlo, el sitio volverá a detectar el dialecto automáticamente en tu siguiente visita.
      </p>

      {/* 5. Cambios */}
      <h2>5. Cambios a esta política</h2>
      <p>
        Cualquier cambio en el uso de cookies o almacenamiento local quedará reflejado en esta página con
        la fecha de actualización al inicio. Si introdujéramos cookies de terceros adicionales, lo
        notificaremos con antelación y, si fuera necesario, solicitaremos tu consentimiento.
      </p>

      <p>
        Para cualquier consulta sobre cookies o almacenamiento, escríbenos a{' '}
        <a href="mailto:derechos@enautomatico.pro">derechos@enautomatico.pro</a>.
      </p>
    </LegalPageChrome>
  );
}
